import {
	Checkbox,
	Group,
	Input,
	Modal,
	ScrollArea,
	Select,
	Skeleton,
	Space,
	Table,
	TextInput,
	Tooltip,
} from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconBuildingStore, IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useBranchMultiSelect } from '../../hooks/useBranchMultiSelect';
import { useModal } from '../../hooks/useModal';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { useBranchOptions } from '../../util/api/commonQueries';
import { undefinedCheck } from '../../util/undefinedCheck';
import { Title } from '../Title';
import { FormButtons } from './FormButtons';
import { KeyedRow } from '../KeyedRow';

type FormBranchMultiSelectorProps = {
	form: UseFormReturnType<any, (values: any) => any>;
	property: string;
	placeHolder?: string;
	preselectedBranchIds?: string[];
	unavailableBranchIds?: string[];
	disabledMessage?: string;
};

export function FormBranchMultiSelector({
	form,
	preselectedBranchIds = [],
	unavailableBranchIds = [],
	disabledMessage = '',
	property,
	placeHolder = '',
}: FormBranchMultiSelectorProps) {
	const pharmaciesQuery = useBranchOptions();

	const placeHolderMessage = useMemo(() => {
		if (placeHolder !== '') {
			return placeHolder;
		}

		return 'Select branches associated with target...';
	}, [placeHolder]);

	const multiSelectModal = useModal();

	const [searchQuery, setSearchQuery] = useState('');
	const [selectedArea, setSelectedArea] = useState<string | null>(null);

	const branchIds = useBranchMultiSelect(
		pharmaciesQuery.data?.value.branches
			.filter((b) => !unavailableBranchIds.includes(b.id.toString()))
			.map((b) => b.id.toString()) ?? [],
		preselectedBranchIds,
	);

	const filteredBranches = useMemo(() => {
		if (!pharmaciesQuery.data) return;

		let pharmacies = pharmaciesQuery.data.value.branches;

		if (searchQuery.replace(' ', '') !== '') {
			pharmacies = pharmacies.filter((b) =>
				b.name?.toLowerCase().includes(searchQuery.toLowerCase()),
			);
		}

		if (selectedArea) {
			pharmacies = pharmacies.filter(
				(b) => selectedArea !== null && b.area.trim() === selectedArea.trim(),
			);
		}

		return pharmacies;
	}, [pharmaciesQuery.data, selectedArea, searchQuery]);

	const displayValue = useMemo(() => {
		if (branchIds.selectedIds.length === 1) {
			return '1 branch selected';
		}

		if (branchIds.selectedIds.length > 1) {
			return `${branchIds.selectedIds.length} branches selected`;
		}

		if (branchIds.selectedIds.length === 0) return '';
	}, [branchIds.selectedIds]);

	const isBranchSelectable = (branchId: number) => {
		const branchIdString = branchId.toString();

		if (preselectedBranchIds.includes(branchIdString)) return true;

		if (unavailableBranchIds.includes(branchIdString)) return false;

		return true;
	};

	return (
		<>
			{!pharmaciesQuery.isLoading ? (
				<>
					<Modal
						opened={multiSelectModal.isOpen}
						onClose={() => {
							branchIds.reset(preselectedBranchIds);
							multiSelectModal.close();
						}}
						title={<Title label={'select branches'} />}
						withCloseButton={true}
						size={'md'}
						centered
					>
						<Group justify="space-between">
							<TextInput
								placeholder={'Search branch...'}
								variant="filled"
								w={'48%'}
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
							<Select
								placeholder={'Select area...'}
								variant="filled"
								data={pharmaciesQuery.data?.value.areaNames}
								value={selectedArea}
								clearable
								onChange={(e) => setSelectedArea(e)}
								w={'48%'}
							/>
						</Group>
						<Space h={15} />
						<ScrollArea h={'30vh'}>
							<Table stickyHeader highlightOnHover>
								<Table.Thead>
									<KeyedRow>
										<Table.Th>
											<Checkbox
												color={'medepos.1'}
												checked={branchIds.allSelected}
												onClick={() =>
													branchIds.allSelected
														? branchIds.reset()
														: branchIds.selectAll()
												}
											/>
										</Table.Th>
										<Table.Th>Name</Table.Th>
										<Table.Th>Area</Table.Th>
									</KeyedRow>
								</Table.Thead>
								<Table.Tbody>
									{filteredBranches
										?.slice() // avoid mutating original array
										.sort((a, b) => {
											const aDisabled = isBranchSelectable(a.id);
											const bDisabled = isBranchSelectable(b.id);
											return Number(bDisabled) - Number(aDisabled); // false < true
										})
										.map((branch) => (
											<KeyedRow
												key={branch.id}
												onClick={() => {
													if (isBranchSelectable(branch.id)) {
														branchIds.toggleId(branch.id.toString());
													}
												}}
											>
												<Table.Td>
													<Tooltip
														label={disabledMessage}
														disabled={isBranchSelectable(branch.id)}
													>
														<Checkbox
															color={'medepos.1'}
															checked={branchIds.isSelected(
																branch.id.toString(),
															)}
															disabled={!isBranchSelectable(branch.id)}
														/>
													</Tooltip>
												</Table.Td>
												<Table.Td>{undefinedCheck(branch.name)}</Table.Td>
												<Table.Td>{undefinedCheck(branch.area)}</Table.Td>
											</KeyedRow>
										))}
								</Table.Tbody>
							</Table>
						</ScrollArea>

						<FormButtons
							close={() => {
								branchIds.reset(preselectedBranchIds);
								multiSelectModal.close();
							}}
							onClick={() => {
								form.setFieldValue(property, branchIds.selectedIds);
								multiSelectModal.close();
							}}
						/>
					</Modal>
					<Input.Wrapper
						label={'Branches'}
						onClick={() => multiSelectModal.open()}
						{...form.getInputProps(property)}
						key={form.key(property)}
					>
						<Input
							leftSection={<IconBuildingStore {...defaultIconStyle} />}
							rightSection={<IconPlus {...defaultIconStyle} />}
							placeholder={placeHolderMessage}
							value={displayValue}
							{...form.getInputProps(property)}
						/>
					</Input.Wrapper>
				</>
			) : (
				<Input.Wrapper label={'Branches'}>
					<Skeleton height={8} mt={6} width="100%" radius={'xl'} h={25} />
				</Input.Wrapper>
			)}
		</>
	);
}
